import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Calendar, Mail, MapPin, Lock, Eye, Database } from 'lucide-react';
import { getEnvironmentConfig } from '@safermark/config';

export default function PrivacyPage() {
  const config = getEnvironmentConfig();
  const currentDate = new Date().toLocaleDateString('en-NZ', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-slate-900">
      {/* Header */}
      <div className="relative bg-black/50 border-b border-cyan-400/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="flex items-center text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-6 w-6 text-cyan-400" />
              <span className="font-bold text-white">Safermark Vaults</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-black/40 backdrop-blur-sm border border-cyan-400/30 rounded-2xl p-8 lg:p-12">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <div className="flex items-center space-x-4 text-gray-400">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Last updated: {currentDate}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                <span>New Zealand</span>
              </div>
            </div>
          </div>

          <div className="prose prose-invert prose-cyan max-w-none">
            <div className="space-y-8 text-gray-300 leading-relaxed">
              
              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">1. Introduction</h2>
                <p>
                  Safermark Vaults ("we," "our," or "us") is committed to protecting your privacy and ensuring the security 
                  of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                  your information when you use our secure bookmark and note management service ("Service").
                </p>
                <p className="mt-3">
                  We are a New Zealand registered company providing services globally. This policy complies with 
                  New Zealand privacy laws and is designed to meet international privacy standards including GDPR, 
                  CCPA, and other applicable privacy regulations. By using our Service, you consent to the data 
                  practices described in this policy.
                </p>
              </section>

              <section>
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-lg p-6 border border-cyan-400/20 mb-6">
                  <div className="flex items-center mb-4">
                    <Lock className="h-6 w-6 text-cyan-400 mr-3" />
                    <h3 className="text-xl font-semibold text-white">Zero-Knowledge Architecture</h3>
                  </div>
                  <p className="text-cyan-100">
                    <strong>Important:</strong> Safermark Vaults uses zero-knowledge encryption, meaning we cannot access, 
                    read, or decrypt your bookmarks, notes, or any stored content. Your data is encrypted on your device 
                    before it reaches our servers, and only you have the decryption keys.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-medium text-white mb-3">2.1 Account Information</h3>
                <p>When you create an account, we collect:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Email address (for account creation and communication)</li>
                  <li>Username (optional, if provided)</li>
                  <li>Password (encrypted and stored securely - we cannot see your actual password)</li>
                  <li>Account creation and last login timestamps</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-6">2.2 Usage Information</h3>
                <p>We automatically collect certain technical information:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>IP address and general location (city/country level)</li>
                  <li>Browser type and version</li>
                  <li>Device type and operating system</li>
                  <li>Service usage patterns and feature utilization</li>
                  <li>Error logs and performance metrics</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-6">2.3 Payment Information</h3>
                <p>For premium subscriptions:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Payment processing is handled by Stripe (we never store payment card details)</li>
                  <li>We receive confirmation of successful payments and subscription status</li>
                  <li>Billing address may be collected for tax compliance</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-6">2.4 Encrypted Content</h3>
                <p>Your actual data (bookmarks, notes, content) is:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Encrypted on your device before transmission</li>
                  <li>Stored in encrypted form on our servers</li>
                  <li>Never accessible to us or our staff</li>
                  <li>Only decryptable with your password/key</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">3. How We Use Your Information</h2>
                <p>We use the collected information for:</p>
                
                <h3 className="text-xl font-medium text-white mb-3 mt-4">3.1 Service Provision</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Creating and maintaining your account</li>
                  <li>Providing secure data storage and synchronization</li>
                  <li>Processing subscription payments</li>
                  <li>Delivering customer support</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">3.2 Service Improvement</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Analyzing usage patterns to improve features</li>
                  <li>Monitoring system performance and reliability</li>
                  <li>Debugging and fixing technical issues</li>
                  <li>Developing new features and enhancements</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">3.3 Communication</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Sending important service notifications</li>
                  <li>Responding to customer inquiries</li>
                  <li>Providing technical support</li>
                  <li>Notifying about policy changes (with consent)</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">3.4 Legal Compliance</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Complying with applicable laws and regulations</li>
                  <li>Responding to legal requests when required</li>
                  <li>Protecting against fraud and abuse</li>
                  <li>Maintaining security and system integrity</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">4. Information Sharing and Disclosure</h2>
                <p>We do not sell, rent, or trade your personal information. We may share information only in these limited circumstances:</p>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">4.1 Service Providers</h3>
                <p>We work with trusted third-party service providers:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>AWS:</strong> Cloud hosting and storage services</li>
                  <li><strong>Stripe:</strong> Payment processing and billing</li>
                  <li><strong>Email services:</strong> For account notifications and support</li>
                </ul>
                <p className="mt-2">These providers are contractually bound to protect your information and use it only for the services they provide to us.</p>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">4.2 Legal Requirements</h3>
                <p>We may disclose information when required by law or to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Comply with legal processes or government requests</li>
                  <li>Protect our rights, property, or safety</li>
                  <li>Protect the rights, property, or safety of our users</li>
                  <li>Investigate fraud or security issues</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3 mt-4">4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                  as part of the business assets, subject to this privacy policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">5. Data Security</h2>
                <div className="bg-slate-800/50 rounded-lg p-6 border border-cyan-400/20">
                  <div className="flex items-center mb-4">
                    <Database className="h-5 w-5 text-cyan-400 mr-2" />
                    <h3 className="text-lg font-medium text-white">Security Measures</h3>
                  </div>
                  <ul className="space-y-2">
                    <li><strong className="text-cyan-400">AES-256 Encryption:</strong> Industry-standard encryption for all stored data</li>
                    <li><strong className="text-cyan-400">PBKDF2:</strong> 310,000 iterations for key derivation</li>
                    <li><strong className="text-cyan-400">HTTPS/TLS:</strong> Secure transmission of all data</li>
                    <li><strong className="text-cyan-400">Access Controls:</strong> Strict access controls and authentication</li>
                    {/*<li><strong className="text-cyan-400">Regular Audits:</strong> Security assessments and vulnerability testing</li>*/}
                    <li><strong className="text-cyan-400">Data Isolation:</strong> Each user's data is cryptographically isolated</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">6. Your Privacy Rights</h2>
                <p>You have various rights regarding your personal information, which may vary depending on your location:</p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-3">
                    <h4 className="text-lg font-medium text-white">Access & Information Rights</h4>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Access your personal information</li>
                      <li>Request a copy of your data</li>
                      <li>Know how your data is used and processed</li>
                      <li>Understand who your data is shared with</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="text-lg font-medium text-white">Control & Deletion Rights</h4>
                    <ul className="list-disc pl-4 space-y-1">
                      <li>Correct inaccurate information</li>
                      <li>Delete your account and data</li>
                      <li>Opt-out of communications</li>
                      <li>Data portability (where applicable)</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 mt-6 border border-cyan-400/20">
                  <h5 className="font-medium text-white mb-2">Regional Privacy Rights:</h5>
                  <ul className="text-sm space-y-1">
                    <li><strong className="text-cyan-400">EU/EEA users:</strong> GDPR rights including right to rectification, erasure, and data portability</li>
                    <li><strong className="text-cyan-400">California residents:</strong> CCPA rights including right to know, delete, and opt-out</li>
                    <li><strong className="text-cyan-400">All users:</strong> Rights under our global privacy standards</li>
                  </ul>
                </div>

                <p className="mt-4">
                  To exercise these rights, contact us at{' '}
                  <a href={`mailto:${config.contactEmail}`} className="text-cyan-400 hover:text-cyan-300 underline">
                    {config.contactEmail}
                  </a>. We will respond within the timeframes required by applicable law (typically 20-30 days).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">7. Data Retention</h2>
                <p>We retain your information for as long as necessary to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Provide the Service to you</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce agreements</li>
                  <li>Maintain business records as required by law</li>
                </ul>
                <p className="mt-3">
                  When you delete your account, we will delete your personal information within 30 days, 
                  except where retention is required by law. Your encrypted content is immediately inaccessible 
                  upon account deletion.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">8. International Data Transfers</h2>
                <p>
                  Your encrypted data may be stored and processed outside New Zealand through our cloud service providers:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li><strong>AWS:</strong> Data stored in secure AWS data centers (primarily US regions)</li>
                  <li><strong>Stripe:</strong> Payment data processed through Stripe's global infrastructure</li>
                  <li><strong>Encryption:</strong> All personal data is encrypted before any international transfer</li>
                  <li><strong>Contractual Safeguards:</strong> Our service providers maintain appropriate data protection standards</li>
                </ul>
                <p className="mt-3">
                  Due to our zero-knowledge architecture, your bookmarks and notes remain encrypted and unreadable 
                  regardless of where they are stored.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">9. Cookies and Tracking</h2>
                <p>We use cookies and similar technologies for:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Essential service functionality (authentication, preferences)</li>
                  <li>Security and fraud prevention</li>
                  <li>Service analytics and performance monitoring</li>
                </ul>
                <p className="mt-3">
                  You can control cookie settings through your browser. However, disabling essential cookies 
                  may impact Service functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">10. Children's Privacy</h2>
                <p>
                  Our Service is not intended for children under 13 years of age. We do not knowingly collect 
                  personal information from children under 13. If you are a parent or guardian and believe your 
                  child has provided us with personal information, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Updating the "Last updated" date</li>
                  <li>Sending you an email notification (for material changes)</li>
                </ul>
                <p className="mt-3">
                  Your continued use of the Service after any changes constitutes acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-4">12. Contact Information</h2>
                <div className="bg-slate-800/50 rounded-lg p-6 border border-cyan-400/20">
                  <p className="mb-4">If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-3 text-cyan-400" />
                      <div>
                        <strong className="text-white">Email:</strong>{' '}
                        <a href={`mailto:${config.contactEmail}`} className="text-cyan-400 hover:text-cyan-300 underline">
                          {config.contactEmail}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-3 text-cyan-400" />
                      <div>
                        <strong className="text-white">Company Registration:</strong> New Zealand (providing global services)
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-3 text-cyan-400" />
                      <div>
                        <strong className="text-white">Privacy Officer:</strong> Available via email for privacy-related inquiries worldwide
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="border-t border-gray-600 pt-6 mt-8">
                <p className="text-sm text-gray-400">
                  This Privacy Policy is effective as of {currentDate}. 
                  By using Safermark Vaults, you acknowledge that you have read and understood this Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}